const API_BASE = process.env.REACT_APP_SHREQ_API_BASE || 'https://hpel46n3xi.execute-api.us-west-2.amazonaws.com/beta';
const API_KEY = process.env.REACT_APP_SHREQ_API_KEY;

function getToken() {
  return localStorage.getItem('token');
}

function buildHeaders(includeAuth = true) {
  const headers = { 'X-Api-Key': API_KEY || '' };
  if (includeAuth) {
    const token = getToken();
    if (token) {
      headers['Authorization'] = token;
    }
  }
  return headers;
}

let refreshPromise = null;

export async function tryRefreshToken() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = _doRefresh();
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

async function _doRefresh() {
  const refreshToken = localStorage.getItem('refreshToken');
  const username = localStorage.getItem('cognitoUsername') || localStorage.getItem('email');
  if (!refreshToken || !username) return false;

  try {
    const data = await apiPostNoAuth('/auth/refresh', { refreshToken, username });
    localStorage.setItem('token', data.idToken);
    localStorage.setItem('accessToken', data.accessToken);
    window.dispatchEvent(new Event('token-refreshed'));
    return true;
  } catch (err) {
    console.error('Token refresh failed:', err);
    return false;
  }
}

export function getTokenExpMs() {
  const token = localStorage.getItem('token');
  if (!token) return 0;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return (payload.exp || 0) * 1000;
  } catch {
    return 0;
  }
}

export function redirectToLogin() {
  localStorage.clear();
  const returnTo = window.location.pathname + window.location.search;
  window.location.href = `/login?returnTo=${encodeURIComponent(returnTo)}`;
}

const inflightGets = new Map();

async function _doGet(url) {
  const headers = buildHeaders();
  const res = await fetch(url, { headers });

  if (res.status === 401) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      const retryHeaders = buildHeaders();
      const retryRes = await fetch(url, { headers: retryHeaders });
      if (!retryRes.ok) {
        const text = await retryRes.text();
        throw new Error(`API ${retryRes.status}: ${text}`);
      }
      return retryRes.json();
    }
    redirectToLogin();
    throw new Error('Session expired');
  }

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API ${res.status}: ${text}`);
  }
  return res.json();
}

export const apiGet = async (path, query = {}) => {
  const url = new URL(`${API_BASE}${path}`);
  Object.entries(query).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') url.searchParams.append(k, String(v));
  });

  const key = url.toString();
  if (inflightGets.has(key)) {
    return inflightGets.get(key);
  }

  const promise = _doGet(key).finally(() => {
    inflightGets.delete(key);
  });
  inflightGets.set(key, promise);
  return promise;
};

export const apiPost = async (path, body = {}, query = {}) => {
  const url = new URL(`${API_BASE}${path}`);
  Object.entries(query).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') url.searchParams.append(k, String(v));
  });

  const headers = { ...buildHeaders(), 'Content-Type': 'application/json' };
  const res = await fetch(url.toString(), {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (res.status === 401) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      const retryHeaders = { ...buildHeaders(), 'Content-Type': 'application/json' };
      const retryRes = await fetch(url.toString(), {
        method: 'POST',
        headers: retryHeaders,
        body: JSON.stringify(body),
      });
      if (!retryRes.ok) {
        const text = await retryRes.text();
        throw new Error(`API ${retryRes.status}: ${text}`);
      }
      return retryRes.json();
    }
    redirectToLogin();
    throw new Error('Session expired');
  }

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API ${res.status}: ${text}`);
  }
  return res.json();
};

export const apiPostNoAuth = async (path, body = {}) => {
  const url = new URL(`${API_BASE}${path}`);
  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Api-Key': API_KEY || '',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API ${res.status}: ${text}`);
  }
  return res.json();
};
